import { useState, useEffect } from 'react';
import { Dialog, DialogPanel } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link, useLocation } from "react-router-dom";

const Navbar = ({ variant = 'light' }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isDark = variant === 'dark';
  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { to: '/products/pool', label: 'Devdesk Pool' },
    { to: '/products/auditra', label: 'Auditra' },
    { to: '/services', label: 'Services' },
    { to: '/work', label: 'Work' },
    { to: '/about', label: 'About' },
  ];

  return (
    <div>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/80 backdrop-blur-xl shadow-[0_1px_0_0_rgba(0,0,0,0.05)]'
          : isDark ? 'bg-transparent' : 'bg-white/0'
      }`}>
        <div className="px-6 py-3 mx-auto sm:px-6 lg:px-16 max-w-7xl">
          <div className="flex items-center justify-between h-14 lg:h-16">
            <Link to="/" className="flex items-center gap-2 group">
              <img className="w-auto h-7 transition-transform duration-300 group-hover:scale-105" src="/img/logo.png" alt="DevDesk" />
            </Link>

            <div className="flex lg:hidden">
              <button
                type="button"
                className={`inline-flex items-center justify-center rounded-xl p-2 transition-colors ${
                  scrolled || !isDark ? 'text-gray-900 hover:bg-gray-100' : 'text-white hover:bg-white/10'
                }`}
                onClick={() => setMobileMenuOpen(true)}
              >
                <Bars3Icon className="h-7 w-7" aria-hidden="true" />
              </button>
            </div>

            <nav className="hidden lg:flex lg:items-center lg:gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`relative px-4 py-2 text-[15px] font-medium rounded-xl transition-all duration-300 ${
                    isActive(link.to)
                      ? 'text-[#0186ef] bg-blue-50'
                      : scrolled || !isDark
                        ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        : 'text-gray-300 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="hidden lg:flex lg:items-center lg:gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold text-white bg-[#0186ef] rounded-xl hover:bg-[#0070d0] transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/25 hover:-translate-y-0.5"
              >
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
      </header>

      <Dialog className="lg:hidden" open={mobileMenuOpen} onClose={setMobileMenuOpen}>
        <div className="fixed inset-0 z-50 bg-black/20 backdrop-blur-sm" />
        <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm">
          <div className="flex items-center justify-between">
            <Link to="/" className="p-1.5" onClick={() => setMobileMenuOpen(false)}>
              <img className="w-auto h-7" src="/img/logo.png" alt="DevDesk" />
            </Link>
            <button
              type="button"
              className="rounded-xl p-2 text-gray-700 hover:bg-gray-100"
              onClick={() => setMobileMenuOpen(false)}
            >
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-8 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`block px-4 py-3 text-base font-medium rounded-xl transition-all duration-200 ${
                  isActive(link.to)
                    ? 'text-[#0186ef] bg-blue-50'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 mt-4 border-t border-gray-100">
              <Link
                to="/contact"
                className="block w-full text-center px-4 py-3 text-base font-semibold text-white bg-[#0186ef] rounded-xl hover:bg-[#0070d0]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Get in Touch
              </Link>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </div>
  );
};

export default Navbar;
